import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { createManualServiceRequest } from '../../services/api';
import { APPLIANCE_TYPES, PRIORITIES } from '../../lib/serviceRequests';

const NOT_SET = '__none__';

const UNASSIGNED = '__none__';

const EMPTY = {
  first_name: '',
  last_name: '',
  phone: '',
  email: '',
  address: '',
  problem_description: '',
  appliance_type: NOT_SET,
  brand: '',
  model: '',
  priority: '',
  under_warranty: NOT_SET,
  appointment_date: '',
  appointment_time: '',
  assigned_employee_id: UNASSIGNED,
  notes: '',
  send_confirmation: true,
};

// Appel de service saisi par l'équipe (téléphone, courriel, client sur place).
export const NewServiceRequestDialog = ({ open, onOpenChange, onCreated, employees = [], canAssign }) => {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const set = (field) => (event) => setForm({ ...form, [field]: event.target.value });
  const canConfirm = Boolean(form.email.trim() && form.appointment_date && form.appointment_time);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.phone.trim() && !form.email.trim()) {
      toast.error('Indiquez un téléphone ou un courriel');
      return;
    }
    if (Boolean(form.appointment_date) !== Boolean(form.appointment_time)) {
      toast.error("Indiquez la date et l'heure du rendez-vous, ou aucune des deux");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...Object.fromEntries(
          Object.entries(form).map(([key, value]) => [key, typeof value === 'string' ? value.trim() || null : value])
        ),
        last_name: form.last_name.trim(),
        assigned_employee_id: form.assigned_employee_id === UNASSIGNED ? null : form.assigned_employee_id,
        appliance_type: form.appliance_type === NOT_SET ? null : form.appliance_type,
        under_warranty: form.under_warranty === NOT_SET ? null : form.under_warranty === 'yes',
        priority: form.priority || null,
        send_confirmation: canConfirm && form.send_confirmation,
      };
      const { data } = await createManualServiceRequest(payload);
      if (data.confirmation === 'sent') toast.success('Appel créé, confirmation envoyée au client');
      else if (data.confirmation === 'failed') toast.warning("Appel créé, mais la confirmation n'a pas pu être envoyée");
      else toast.success('Appel de service créé');
      setForm(EMPTY);
      onOpenChange(false);
      onCreated?.(data);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Vérifiez les champs (courriel valide, adresse, description)');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Nouvel appel de service</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="new-service-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Prénom *</Label>
              <Input value={form.first_name} onChange={set('first_name')} maxLength={80} required data-testid="new-service-first-name" />
            </div>
            <div>
              <Label>Nom</Label>
              <Input value={form.last_name} onChange={set('last_name')} maxLength={80} />
            </div>
            <div>
              <Label>Téléphone</Label>
              <Input type="tel" value={form.phone} onChange={set('phone')} maxLength={40} data-testid="new-service-phone" />
            </div>
            <div>
              <Label>Courriel</Label>
              <Input type="email" value={form.email} onChange={set('email')} data-testid="new-service-email" />
            </div>
          </div>
          <div>
            <Label>Adresse *</Label>
            <Input value={form.address} onChange={set('address')} maxLength={300} required data-testid="new-service-address" />
          </div>
          <div>
            <Label>Problème *</Label>
            <Textarea
              value={form.problem_description}
              onChange={set('problem_description')}
              maxLength={4000}
              required
              placeholder="Appareil, symptômes, marque et modèle..."
              data-testid="new-service-problem"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <Label>Appareil</Label>
              <Select value={form.appliance_type} onValueChange={(value) => setForm({ ...form, appliance_type: value })}>
                <SelectTrigger data-testid="new-service-appliance">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NOT_SET}>Non précisé</SelectItem>
                  {APPLIANCE_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Marque</Label>
              <Input value={form.brand} onChange={set('brand')} maxLength={80} />
            </div>
            <div>
              <Label>Modèle</Label>
              <Input value={form.model} onChange={set('model')} maxLength={80} />
            </div>
          </div>
          <div>
            <Label>Code de priorité</Label>
            <div className="grid grid-cols-3 gap-2 mt-1">
              {PRIORITIES.map((item) => (
                <Button
                  key={item.value}
                  type="button"
                  variant={form.priority === item.value ? 'default' : 'outline'}
                  className={form.priority === item.value ? `${item.button} border-transparent shadow` : ''}
                  onClick={() => setForm({ ...form, priority: form.priority === item.value ? '' : item.value })}
                  data-testid={`new-service-priority-${item.value}`}
                >
                  <span className={`w-3 h-3 rounded-full mr-2 ${item.dot} ${form.priority === item.value ? 'ring-2 ring-white' : ''}`}></span>
                  {item.label}
                </Button>
              ))}
            </div>
            <p className="text-xs text-slate-500 mt-1">Rouge : urgent · Jaune : prioritaire · Vert : peut attendre</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Sous garantie</Label>
              <Select value={form.under_warranty} onValueChange={(value) => setForm({ ...form, under_warranty: value })}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NOT_SET}>Ne sait pas</SelectItem>
                  <SelectItem value="yes">Oui</SelectItem>
                  <SelectItem value="no">Non</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Date du rendez-vous</Label>
              <Input type="date" value={form.appointment_date} onChange={set('appointment_date')} data-testid="new-service-date" />
            </div>
            <div>
              <Label>Heure</Label>
              <Input type="time" value={form.appointment_time} onChange={set('appointment_time')} data-testid="new-service-time" />
            </div>
          </div>
          {canAssign && employees.length > 0 && (
            <div>
              <Label>Technicien</Label>
              <Select value={form.assigned_employee_id} onValueChange={(value) => setForm({ ...form, assigned_employee_id: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={UNASSIGNED}>À assigner plus tard</SelectItem>
                  {employees.map((employee) => (
                    <SelectItem key={employee.id} value={employee.id}>
                      {employee.first_name} {employee.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label>Notes internes</Label>
            <Textarea value={form.notes} onChange={set('notes')} maxLength={4000} className="min-h-[60px]" />
          </div>
          <label className={`flex items-start gap-3 rounded-lg border p-3 ${canConfirm ? 'border-slate-200' : 'border-slate-100 opacity-60'}`}>
            <input
              type="checkbox"
              checked={canConfirm && form.send_confirmation}
              onChange={(e) => setForm({ ...form, send_confirmation: e.target.checked })}
              disabled={!canConfirm}
              className="mt-1 h-4 w-4 accent-[#00b4d8]"
              data-testid="new-service-confirm"
            />
            <span className="text-sm">
              <span className="font-medium text-[#0f172a]">Envoyer la confirmation au client</span>
              <span className="block text-slate-500">
                {canConfirm ? 'Courriel avec la date, l\'heure et l\'adresse.' : 'Nécessite un courriel, une date et une heure.'}
              </span>
            </span>
          </label>
          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Annuler
            </Button>
            <Button type="submit" disabled={saving} className="bg-[#00b4d8] hover:bg-[#009bb8]" data-testid="new-service-submit">
              {saving ? 'Création...' : "Créer l'appel"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default NewServiceRequestDialog;
